import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { RefreshCw } from 'lucide-react'
import { getEvents, type AccessEvent, type EventFilters } from '@/api/events'
import { toast } from 'sonner'
import { ApiError } from '@/lib/api-error'

type Period = 'today' | 'yesterday' | 'week'
type EventType = '' | 'ACCESS_GRANTED' | 'ACCESS_DENIED'

const VERIFY_MODE_LABELS: Record<string, string> = {
  faceRecognition: '🧑 Cara',
  fingerprint:     '👆 Huella',
  card:            '💳 Tarjeta',
  cardAndPin:      '💳+🔢 Tarjeta+PIN',
  pin:             '🔢 PIN',
}

function periodDates(period: Period): { startDate: string; endDate: string } {
  const now = new Date()
  if (period === 'today') {
    const s = new Date(now); s.setHours(0,0,0,0)
    const e = new Date(now); e.setHours(23,59,59,0)
    return { startDate: s.toISOString().slice(0,16), endDate: e.toISOString().slice(0,16) }
  }
  if (period === 'yesterday') {
    const s = new Date(now); s.setDate(s.getDate()-1); s.setHours(0,0,0,0)
    const e = new Date(now); e.setDate(e.getDate()-1); e.setHours(23,59,59,0)
    return { startDate: s.toISOString().slice(0,16), endDate: e.toISOString().slice(0,16) }
  }
  // week
  const s = new Date(now); s.setDate(s.getDate()-7); s.setHours(0,0,0,0)
  const e = new Date(now); e.setHours(23,59,59,0)
  return { startDate: s.toISOString().slice(0,16), endDate: e.toISOString().slice(0,16) }
}

function relativeTime(dateStr: string): string {
  const now = new Date()
  const d = new Date(dateStr)
  const todayStr = now.toISOString().split('T')[0]
  const yesterdayStr = new Date(now.getTime() - 86400000).toISOString().split('T')[0]
  const timeStr = d.toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' })
  if (dateStr.startsWith(todayStr)) return `hoy ${timeStr}`
  if (dateStr.startsWith(yesterdayStr)) return `ayer ${timeStr}`
  return d.toLocaleDateString('es-CO', { day: 'numeric', month: 'short' }) + ` ${timeStr}`
}

function verifyMode(raw: string | null | undefined): string {
  if (!raw) return '—'
  return VERIFY_MODE_LABELS[raw] ?? raw
}

const LIMIT = 50

export function EventList() {
  const navigate = useNavigate()
  const [events, setEvents] = useState<AccessEvent[]>([])
  const [loading, setLoading] = useState(true)
  const [offset, setOffset] = useState(0)
  const [total, setTotal] = useState(0)
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)
  const [period, setPeriod] = useState<Period>('today')
  const [eventType, setEventType] = useState<EventType>('')
  const [nameFilter, setNameFilter] = useState('')

  const filtersRef = useRef({ period, eventType, nameFilter, offset })
  useEffect(() => { filtersRef.current = { period, eventType, nameFilter, offset } })

  const load = useCallback((off: number) => {
    const f = filtersRef.current
    const { startDate, endDate } = periodDates(f.period)
    const filters: EventFilters = {
      startDate: startDate + ':00',
      endDate: endDate + ':59',
      ...(f.eventType && { eventType: f.eventType }),
      ...(f.nameFilter && { userName: f.nameFilter }),
      limit: LIMIT,
      offset: off,
    }
    setLoading(true)
    getEvents(filters)
      .then(r => {
        setEvents(r.events)
        setTotal((r as { events: AccessEvent[]; total?: number }).total ?? r.events.length + off)
        setLastUpdated(new Date())
      })
      .catch(e => toast.error(e instanceof ApiError ? e.message : 'Error'))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => { load(0) }, [load])

  // Auto-refresh every 30s on page 1
  useEffect(() => {
    const id = setInterval(() => { if (filtersRef.current.offset === 0) load(0) }, 30000)
    return () => clearInterval(id)
  }, [load])

  function handlePeriodChange(p: Period) { setPeriod(p); setOffset(0); filtersRef.current.period = p; filtersRef.current.offset = 0; load(0) }
  function handleTypeChange(t: EventType) { setEventType(t); setOffset(0); filtersRef.current.eventType = t; filtersRef.current.offset = 0; load(0) }
  function handleSearch() { setOffset(0); filtersRef.current.offset = 0; load(0) }

  const granted = events.filter(e => e.event_type === 'ACCESS_GRANTED').length
  const denied  = events.filter(e => e.event_type === 'ACCESS_DENIED').length
  const pages   = Math.ceil(total / LIMIT)
  const currentPage = Math.floor(offset / LIMIT) + 1

  return (
    <div className="flex flex-col gap-4">
      {/* Header + stats */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-text-primary font-bold text-lg">Eventos de acceso</h1>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-emerald-600 dark:text-emerald-400 text-xs">Auto-actualiza cada 30s</span>
            {lastUpdated && <span className="text-text-muted text-xs">· {lastUpdated.toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span>}
          </div>
        </div>
        <div className="flex gap-3">
          <div className="bg-surface border border-border rounded-lg px-3 py-2 text-center">
            <p className="text-text-primary text-sm font-bold">{events.length}</p>
            <p className="text-text-muted text-[10px]">Total</p>
          </div>
          <div className="bg-emerald-50 dark:bg-emerald-950/25 border border-emerald-200 dark:border-emerald-900/50 rounded-lg px-3 py-2 text-center">
            <p className="text-emerald-700 dark:text-emerald-400 text-sm font-bold">{granted}</p>
            <p className="text-text-muted text-[10px]">Concedidos</p>
          </div>
          <div className="bg-red-50 dark:bg-red-950/25 border border-red-200 dark:border-red-900/50 rounded-lg px-3 py-2 text-center">
            <p className="text-red-700 dark:text-red-400 text-sm font-bold">{denied}</p>
            <p className="text-text-muted text-[10px]">Denegados</p>
          </div>
        </div>
      </div>

      {/* Compact filter bar */}
      <div className="bg-surface border border-border rounded-xl px-4 py-2.5 flex items-center gap-3 flex-wrap">
        {/* Period chips */}
        <div className="flex gap-1.5">
          {(['today','yesterday','week'] as Period[]).map(p => (
            <button key={p} onClick={() => handlePeriodChange(p)} className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${period === p ? 'bg-accent/10 text-accent border-accent/30' : 'text-text-muted border-border hover:text-text-primary'}`}>
              {p === 'today' ? 'Hoy' : p === 'yesterday' ? 'Ayer' : 'Esta semana'}
            </button>
          ))}
        </div>
        <div className="w-px h-4 bg-border" />
        {/* Type chips */}
        <div className="flex gap-1.5">
          <button onClick={() => handleTypeChange('')} className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${eventType === '' ? 'bg-accent/10 text-accent border-accent/30' : 'text-text-muted border-border hover:text-text-primary'}`}>Todos</button>
          <button onClick={() => handleTypeChange('ACCESS_GRANTED')} className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${eventType === 'ACCESS_GRANTED' ? 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400 border-emerald-300 dark:border-emerald-800' : 'text-text-muted border-border hover:text-text-primary'}`}>✓ Concedidos</button>
          <button onClick={() => handleTypeChange('ACCESS_DENIED')} className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${eventType === 'ACCESS_DENIED' ? 'bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-400 border-red-300 dark:border-red-800' : 'text-text-muted border-border hover:text-text-primary'}`}>✗ Denegados</button>
        </div>
        <div className="w-px h-4 bg-border" />
        {/* Name search */}
        <input value={nameFilter} onChange={e => setNameFilter(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSearch()}
          placeholder="Filtrar por nombre..." className="flex-1 min-w-32 bg-transparent text-text-primary text-xs placeholder:text-text-muted focus:outline-none" />
        <button onClick={() => load(0)} className="text-text-muted hover:text-text-primary transition-colors" aria-label="Actualizar">
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>

      {loading ? (
        <p className="text-text-muted text-sm">Cargando...</p>
      ) : (
        <>
          <div className="bg-surface rounded-xl border border-border overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-background/50">
                  <th className="text-left px-4 py-3 text-text-muted text-xs font-bold uppercase tracking-wide">Fecha / Hora</th>
                  <th className="text-left px-4 py-3 text-text-muted text-xs font-bold uppercase tracking-wide">Miembro</th>
                  <th className="text-left px-4 py-3 text-text-muted text-xs font-bold uppercase tracking-wide">Resultado</th>
                  <th className="text-left px-4 py-3 text-text-muted text-xs font-bold uppercase tracking-wide">Puerta</th>
                  <th className="text-left px-4 py-3 text-text-muted text-xs font-bold uppercase tracking-wide">Modo</th>
                </tr>
              </thead>
              <tbody>
                {events.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-text-muted text-xs">Sin eventos en este período</td></tr>
                )}
                {events.map(ev => {
                  const isGranted = ev.event_type === 'ACCESS_GRANTED'
                  const ini = ev.user_name ? ev.user_name.split(' ').slice(0,2).map((w: string) => w[0]).join('').toUpperCase() : '?'
                  return (
                    <tr key={ev.id} className={`border-b border-border last:border-0 transition-colors ${!isGranted ? 'bg-red-50 dark:bg-red-950/15 border-l-2 border-l-red-500/60' : 'hover:bg-background/30'}`}>
                      <td className="px-4 py-3">
                        <p className="text-text-primary text-xs font-medium">{relativeTime(ev.occurred_at)}</p>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 text-[9px] font-bold ${isGranted ? 'bg-accent/20 text-accent border border-accent/30' : 'bg-red-100 dark:bg-red-950/50 text-red-700 dark:text-red-400 border border-red-300 dark:border-red-900/50'}`}>
                            {ini}
                          </div>
                          <div>
                            <p className="text-text-primary text-xs font-semibold">{ev.user_name ?? 'Desconocido'}</p>
                            <p className="text-text-muted text-[10px]">{ev.employee_no ?? '—'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs border ${isGranted ? 'bg-emerald-50 dark:bg-emerald-950/25 border-emerald-200 dark:border-emerald-900/50 text-emerald-700 dark:text-emerald-400' : 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-900/60 text-red-700 dark:text-red-400'}`}>
                          {isGranted ? '✓ Concedido' : '✗ Denegado'}
                        </span>
                        {!isGranted && ev.user_id && (
                          <button onClick={() => navigate(`/members/${ev.user_id}`)} className="block text-[10px] text-accent hover:underline mt-1">
                            Ver perfil →
                          </button>
                        )}
                      </td>
                      <td className="px-4 py-3 text-text-muted text-xs">
                        {ev.door_no === 1 ? 'Entrada principal' : ev.door_no ?? '—'}
                      </td>
                      <td className="px-4 py-3 text-text-muted text-xs">{verifyMode(ev.verify_mode)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between">
            <p className="text-text-muted text-xs">Mostrando {offset + 1}–{Math.min(offset + LIMIT, total)} de {total} eventos · página {currentPage} de {pages || 1}</p>
            <div className="flex gap-2">
              <button onClick={() => { const o = Math.max(0, offset - LIMIT); setOffset(o); load(o) }} disabled={offset === 0}
                className="border border-border text-text-muted text-xs px-3 py-1.5 rounded-md disabled:opacity-40 hover:text-text-primary transition-colors">
                ← Anterior
              </button>
              <button onClick={() => { const o = offset + LIMIT; setOffset(o); load(o) }} disabled={events.length < LIMIT}
                className="border border-border text-text-muted text-xs px-3 py-1.5 rounded-md disabled:opacity-40 hover:text-text-primary transition-colors">
                Siguiente →
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
